/* eslint-disable complexity */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import Button from 'carbon-react/lib/components/button';
import Form, { FormProps } from 'carbon-react/lib/components/form';

export enum FooterMode {
  NONE = 'none',
  EMPTY = 'empty',
  NEXT = 'next',
  CREATE = 'create',
  CREATED = 'created',
  UPDATE = 'update'
}

export interface WizardScreenProps extends FormProps {
  footerMode?: FooterMode;
  cancelText?: React.ReactNode;
  closeText?: React.ReactNode;
  createDisabled?: boolean;
  createText?: React.ReactNode;
  nextDisabled?: boolean;
  nextText?: React.ReactNode;
  onCancel?: () => void;
  onCreate?: () => void;
  onNext?: () => void;
  onUpdate?: () => void;
  onView?: () => void;
  updateDisabled?: boolean;
  updateText?: React.ReactNode;
  viewText?: React.ReactNode;
}

export const WizardScreen = ({
  footerMode = FooterMode.NONE,
  cancelText,
  closeText,
  createDisabled,
  createText,
  nextDisabled,
  nextText,
  onCancel,
  onCreate,
  onNext,
  onUpdate,
  onView,
  updateDisabled,
  updateText,
  viewText,
  ...props
}: WizardScreenProps) => {
  const buttons = (() => {
    switch (footerMode) {
      case FooterMode.EMPTY: {
        return (
          // Renders an empty (but visible) footer with correct height
          <div style={{ visibility: 'hidden' }}>
            <Button buttonType="primary" type="button" aria-hidden disabled>
              {' '}
            </Button>
          </div>
        );
      }
      case FooterMode.NEXT: {
        return (
          <>
            <Button buttonType="tertiary" onClick={onCancel}>
              {cancelText ?? <FormattedMessage id="common.cancel" defaultMessage="Cancel" />}
            </Button>
            <Button buttonType="primary" onClick={onNext} disabled={!!nextDisabled}>
              {nextText ?? <FormattedMessage id="common.next" defaultMessage="Next" />}
            </Button>
          </>
        );
      }
      case FooterMode.CREATE: {
        return (
          <>
            <Button buttonType="tertiary" onClick={onCancel}>
              {cancelText ?? <FormattedMessage id="common.cancel" defaultMessage="Cancel" />}
            </Button>
            <Button buttonType="primary" onClick={onCreate} disabled={!!createDisabled}>
              {createText ?? <FormattedMessage id="common.create" defaultMessage="Create" />}
            </Button>
          </>
        );
      }
      case FooterMode.CREATED: {
        return (
          <>
            <Button buttonType="tertiary" onClick={onCancel}>
              {closeText ?? <FormattedMessage id="common.close" defaultMessage="Close" />}
            </Button>
            {onView && (
              <Button buttonType="primary" onClick={onView}>
                {viewText ?? <FormattedMessage id="common.view" defaultMessage="View" />}
              </Button>
            )}
          </>
        );
      }
      case FooterMode.UPDATE: {
        return (
          <>
            <Button buttonType="tertiary" onClick={onCancel}>
              {cancelText ?? <FormattedMessage id="common.cancel" defaultMessage="Cancel" />}
            </Button>
            <Button buttonType="primary" onClick={onUpdate} disabled={!!updateDisabled}>
              {updateText ?? <FormattedMessage id="common.update" defaultMessage="Update" />}
            </Button>
          </>
        );
      }
      default: {
        return null;
      }
    }
  })();

  return (
    <Form
      rightSideButtons={buttons}
      stickyFooter
      buttonAlignment="right"
      footerPadding={{
        p: 2
      }}
      {...props}
    />
  );
};
